'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import { StarRating } from '@ui/components/features/review/starRating';
import { ResponsiveModal } from '@ui/components/features/selection/responsiveModal';
import { Button } from '@ui/components/ui/button';
import { useCreateReview } from '@website/hooks/useCreateReview';

function formatBookingDateTime(bookingDate?: string, timeSlot?: string): string | undefined {
  if (!bookingDate) return undefined;
  const date = new Date(bookingDate + 'T00:00:00');
  if (isNaN(date.getTime())) return bookingDate;
  const formatted = date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  const startTime = timeSlot?.split('-')[0]?.trim();
  return startTime ? `${formatted}  ${startTime}` : formatted;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    let timeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(check, 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);
  return isMobile;
}

export interface RateSessionData {
  bookingId: string;
  activityId: string;
  activityTitle: string;
  professionalName: string;
  sessionType?: string;
  bookingDate: string;
  timeSlot: string;
  /** When set (e.g. user already tapped stars on activity page), open directly on the review step */
  initialRating?: number;
}

type DrawerStep = 'rate' | 'review' | 'thanks';

const EXPERIENCE_TAG_KEYS = [
  'tagExpertise',
  'tagPunctuality',
  'tagHandling',
  'tagEasyToUnderstand',
  'tagHumours'
] as const;

interface RateSessionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  sessionData: RateSessionData | null;
}

export function RateSessionDrawer({ isOpen, onClose, sessionData }: RateSessionDrawerProps) {
  const t = useTranslations('RateSession');
  const isMobile = useIsMobile();
  const [step, setStep] = useState<DrawerStep>('rate');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const createReview = useCreateReview(sessionData?.activityId ?? '');

  const hasAppliedSessionRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !sessionData) {
      if (!isOpen) hasAppliedSessionRef.current = false;
      return;
    }
    if (hasAppliedSessionRef.current) return;
    hasAppliedSessionRef.current = true;

    const initial = sessionData.initialRating;
    if (typeof initial === 'number' && initial >= 1 && initial <= 5) {
      setRating(initial);
      setStep('review');
    } else {
      setStep('rate');
      setRating(0);
    }
    setComment('');
    setSelectedTags([]);
  }, [isOpen, sessionData]);

  const handleStarSelect = useCallback((value: number) => {
    setRating(value);
    setStep('review');
  }, []);

  const handleAskLater = useCallback(() => {
    setStep('rate');
    setRating(0);
    setComment('');
    setSelectedTags([]);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!sessionData || rating === 0) return;
    try {
      await createReview.mutateAsync({
        rating,
        comment: comment.trim(),
        tags: selectedTags.length > 0 ? selectedTags : undefined
      });
      setStep('thanks');
    } catch {
      // Error handled by mutation state
    }
  }, [sessionData, rating, comment, selectedTags, createReview]);

  const handleDone = useCallback(() => {
    setStep('rate');
    setRating(0);
    setComment('');
    setSelectedTags([]);
    onClose();
  }, [onClose]);

  const handleSkip = useCallback(() => {
    setStep('rate');
    setRating(0);
    setComment('');
    setSelectedTags([]);
    onClose();
  }, [onClose]);

  /** Mobile full-screen review step: back dismisses the flow (return to activity), not step 1 */
  const handleBack = useCallback(() => {
    setStep('rate');
    setRating(0);
    setComment('');
    setSelectedTags([]);
    onClose();
  }, [onClose]);

  if (!sessionData) return null;

  if (step === 'review' && isMobile && isOpen) {
    return (
      <MobileReviewFullPage
        sessionData={sessionData}
        rating={rating}
        comment={comment}
        selectedTags={selectedTags}
        onCommentChange={setComment}
        onRatingChange={setRating}
        onToggleTag={(tag) =>
          setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
        }
        onSubmit={handleSubmit}
        onSkip={handleSkip}
        onBack={handleBack}
        isSubmitting={createReview.isPending}
        error={createReview.error?.message}
        t={t}
      />
    );
  }

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleAskLater}
      title={t('howWasYourExperience')}
      showCloseButton
      drawerClassName="p-0 rounded-t-xl"
      dialogClassName="flex flex-col gap-4 p-0 sm:max-w-md"
    >
      {step === 'rate' && (
        <RateStep
          sessionData={sessionData}
          selectedRating={rating}
          onStarSelect={handleStarSelect}
          onAskLater={handleAskLater}
          t={t}
        />
      )}
      {step === 'review' && (
        <ReviewStep
          sessionData={sessionData}
          rating={rating}
          comment={comment}
          selectedTags={selectedTags}
          onCommentChange={setComment}
          onRatingChange={setRating}
          onToggleTag={(tag) =>
            setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
          }
          onSubmit={handleSubmit}
          onSkip={handleSkip}
          isSubmitting={createReview.isPending}
          error={createReview.error?.message}
          t={t}
        />
      )}
      {step === 'thanks' && <ThanksStep onDone={handleDone} t={t} />}
    </ResponsiveModal>
  );
}

interface RateStepProps {
  sessionData: RateSessionData;
  selectedRating: number;
  onStarSelect: (value: number) => void;
  onAskLater: () => void;
  t: ReturnType<typeof useTranslations<'RateSession'>>;
}

function RateStep({ sessionData, selectedRating, onStarSelect, onAskLater, t }: RateStepProps) {
  return (
    <div className="flex flex-col gap-5 md:px-8 md:py-6">
      <h3 className="text-base md:text-lg font-bold text-neutral-800">{t('howWasYourExperience')}</h3>

      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg md:text-xl font-bold text-neutral-800">{sessionData.activityTitle}</h2>
          <SessionMeta sessionData={sessionData} />
        </div>

        <StarRating value={selectedRating} onChange={onStarSelect} size="lg" />
      </div>

      <Button variant="outline" className="w-full border-primary text-primary" onClick={onAskLater}>
        {t('askMeLater')}
      </Button>
    </div>
  );
}

interface ReviewStepProps {
  sessionData: RateSessionData;
  rating: number;
  comment: string;
  selectedTags: string[];
  onCommentChange: (value: string) => void;
  onRatingChange: (value: number) => void;
  onToggleTag: (tag: string) => void;
  onSubmit: () => void;
  onSkip: () => void;
  isSubmitting: boolean;
  error?: string;
  t: ReturnType<typeof useTranslations<'RateSession'>>;
}

function ReviewStep({
  sessionData,
  rating,
  comment,
  selectedTags,
  onCommentChange,
  onRatingChange,
  onToggleTag,
  onSubmit,
  onSkip,
  isSubmitting,
  error,
  t
}: ReviewStepProps) {
  const tags = EXPERIENCE_TAG_KEYS.map((key) => ({ key, label: t(key) }));

  return (
    <div className="flex flex-col gap-4 md:px-8 md:py-6">
      <h3 className="text-base md:text-lg font-bold text-neutral-800">{t('howWasYourExperience')}</h3>

      <div className="flex flex-col gap-1">
        <p className="text-lg md:text-xl font-bold text-neutral-800">{sessionData.activityTitle}</p>
        <SessionMeta sessionData={sessionData} />
      </div>

      <StarRating value={rating} onChange={onRatingChange} size="lg" />

      <ReviewFormFields
        tags={tags}
        selectedTags={selectedTags}
        onToggleTag={onToggleTag}
        comment={comment}
        onCommentChange={onCommentChange}
        error={error}
        t={t}
      />

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onSkip}
          disabled={isSubmitting}
          className="text-sm font-semibold text-primary hover:underline disabled:opacity-50"
        >
          {t('skipThisTime')}
        </button>
        <Button onClick={onSubmit} disabled={rating === 0 || isSubmitting}>
          {isSubmitting ? t('submitting') : t('submitReview')}
        </Button>
      </div>
    </div>
  );
}

interface MobileReviewFullPageProps {
  sessionData: RateSessionData;
  rating: number;
  comment: string;
  selectedTags: string[];
  onCommentChange: (value: string) => void;
  onRatingChange: (value: number) => void;
  onToggleTag: (tag: string) => void;
  onSubmit: () => void;
  onSkip: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  error?: string;
  t: ReturnType<typeof useTranslations<'RateSession'>>;
}

function MobileReviewFullPage({
  sessionData,
  rating,
  comment,
  selectedTags,
  onCommentChange,
  onRatingChange,
  onToggleTag,
  onSubmit,
  onSkip,
  onBack,
  isSubmitting,
  error,
  t
}: MobileReviewFullPageProps) {
  const tags = EXPERIENCE_TAG_KEYS.map((key) => ({ key, label: t(key) }));

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100">
        <button type="button" onClick={onBack} className="p-1.5 rounded-full hover:bg-neutral-100 transition-colors">
          <svg
            className="size-5 text-neutral-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-base font-semibold text-neutral-900">{t('howWasYourExperience')}</h1>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <p className="text-xl font-medium text-black">{sessionData.activityTitle}</p>
              <SessionMeta sessionData={sessionData} />
            </div>
            <StarRating value={rating} onChange={onRatingChange} size="lg" />
          </div>

          <ReviewFormFields
            tags={tags}
            selectedTags={selectedTags}
            onToggleTag={onToggleTag}
            comment={comment}
            onCommentChange={onCommentChange}
            error={error}
            t={t}
          />
        </div>
      </div>

      {/* Fixed bottom bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-4 border-t border-neutral-200 bg-white">
        <button
          type="button"
          onClick={onSkip}
          disabled={isSubmitting}
          className="text-sm font-semibold text-primary hover:underline disabled:opacity-50"
        >
          {t('skipThisTime')}
        </button>
        <Button onClick={onSubmit} disabled={rating === 0 || isSubmitting}>
          {isSubmitting ? t('submitting') : t('submitReview')}
        </Button>
      </div>
    </div>
  );
}

function SessionMeta({ sessionData }: { sessionData: RateSessionData }) {
  return (
    <div className="flex items-center gap-2 text-xs md:text-sm text-neutral-500">
      <span>{sessionData.professionalName}</span>
      {sessionData.sessionType && (
        <>
          <span className="text-neutral-300">|</span>
          <span>{sessionData.sessionType}</span>
        </>
      )}
      {formatBookingDateTime(sessionData.bookingDate, sessionData.timeSlot) && (
        <>
          <span className="text-neutral-300">|</span>
          <span>{formatBookingDateTime(sessionData.bookingDate, sessionData.timeSlot)}</span>
        </>
      )}
    </div>
  );
}

function ReviewFormFields({
  tags,
  selectedTags,
  onToggleTag,
  comment,
  onCommentChange,
  error,
  t
}: {
  tags: { key: string; label: string }[];
  selectedTags: string[];
  onToggleTag: (tag: string) => void;
  comment: string;
  onCommentChange: (value: string) => void;
  error?: string;
  t: ReturnType<typeof useTranslations<'RateSession'>>;
}) {
  return (
    <>
      <div className="flex flex-col gap-3">
        <p className="text-sm md:text-base font-medium text-neutral-700">{t('whatDidYouLike')}</p>
        <div className="flex flex-wrap gap-3">
          {tags.map(({ key, label }) => {
            const isSelected = selectedTags.includes(label);
            return (
              <button
                key={key}
                type="button"
                onClick={() => onToggleTag(label)}
                className={`h-9 px-3 py-2 rounded-lg border text-sm transition-colors ${
                  isSelected
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-neutral-200 text-neutral-700 bg-white hover:border-neutral-300'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <fieldset className="border border-neutral-300 rounded-lg px-3 pt-1 pb-2">
          <legend className="text-xs text-neutral-700 px-1">{t('addReviewOptional')}</legend>
          <textarea
            value={comment}
            onChange={(e) => onCommentChange(e.target.value)}
            placeholder={t('reviewPlaceholder')}
            className="w-full h-[140px] text-base md:text-sm text-neutral-800 placeholder:text-neutral-400 focus:outline-none resize-none"
            maxLength={500}
          />
        </fieldset>
        <p className="text-xs text-neutral-600">{comment.length}/500</p>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </>
  );
}

interface ThanksStepProps {
  onDone: () => void;
  t: ReturnType<typeof useTranslations<'RateSession'>>;
}

function ThanksStep({ onDone, t }: ThanksStepProps) {
  return (
    <div className="flex flex-col gap-6 items-center px-5 pb-6 pt-4 md:px-8 md:py-8">
      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-100 flex items-center justify-center">
        <svg
          className="w-8 h-8 md:w-10 md:h-10 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <div className="text-center">
        <h3 className="text-xl md:text-2xl font-semibold text-neutral-800">{t('thankYou')}</h3>
        <p className="text-sm md:text-base text-neutral-500 mt-2">{t('thankYouMessage')}</p>
      </div>

      <Button onClick={onDone} className="w-full md:max-w-xs">
        {t('done')}
      </Button>
    </div>
  );
}
